import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { Connection, getConnectionHealthStatuses, Destination, Organization, ProviderType, DestinationType } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, tableClasses, filterClasses, paginationClasses } from "../common/tailwindClassnames";
import { Clear, CopyToClipboard, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import { Tooltip } from "../common/Tooltip";
import { getConnectionStatus } from "./getConnectionStatus";
import SelectInput from "../common/SelectInput";

const HealthLegend = ({ t }: { t: (key: string) => string }) => (
    <div className="text-xs text-left">
        <p className="font-semibold mb-1 text-off-black">{t("Connection Health")}</p>
        <p className="mb-1.5 text-secondary-gray">{t("Connections are analyzed every hour.")}</p>
        {(["healthy", "unhealthy", "disabled"] as const).map((status) => {
            const s = getConnectionStatus(status, false, t);
            return (
                <div key={status} className="flex items-center gap-1.5 mb-1">
                    <span className="flex-shrink-0">{s.icon}</span>
                    <span className="text-secondary-gray">{s.text}</span>
                </div>
            );
        })}
    </div>
);

type ConnectionsProps = {
    canAddConnection: boolean,
    connections: Connection[],
    reloadConnections: boolean,
    organizations: Organization[],
    destinationUrls: string[],
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onSelectConnection: Function,
    onChangeGlobalSearch: Function,
    onChangeStatusFilter: Function,
    statusFilterValue: any,
    onChangeDestinationUrl: Function,
    destinationFilterValue: any,
    loadingDestinationUrls: boolean,
    onChangeOwner: Function,
    ownerFilterValue: any,
    loadingOrganizations: boolean,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    globalSearchStr: string,
    requestSort: Function,
    onViewDetails: Function,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    onClickAdd: MouseEventHandler
}

const ConnectionsList = ({
    canAddConnection,
    connections,
    reloadConnections,
    organizations,
    destinationUrls,
    providerTypes,
    loadingProviderTypes,
    destinationTypes,
    loadingDestinationTypes,
    onSelectConnection,
    onChangeGlobalSearch,
    onChangeStatusFilter,
    statusFilterValue,
    onChangeDestinationUrl,
    destinationFilterValue,
    loadingDestinationUrls,
    onChangeOwner,
    ownerFilterValue,
    loadingOrganizations,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    globalSearchStr,
    onViewDetails,
    requestSort,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    onClickAdd
}: ConnectionsProps) => {
    const { t } = useTranslation();
    const [sortDirection, setSortDirection] = useState(true);
    const [sortByColumn, setSortByColumn] = useState("name");

    const connectionHealthStatuses = getConnectionHealthStatuses(t);

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between">
                <div>
                    <h1 data-testid="connections_page_header" className={textClasses.heading}>
                        {t("Connections")}
                    </h1>
                </div>
                {canAddConnection && <button className={buttonClasses.primary}
                    onClick={onClickAdd}
                >
                    {t("Create Connection")}
                </button>}
            </div>
            {/* Horizontal filter bar */}
            <div className="flex flex-row gap-3 mb-3 flex-wrap">
                {/* Search */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Search")}</span>
                    <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} savedString={globalSearchStr} />
                </div>
                {/* State */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("State")}</span>
                    <SelectInput
                        name="status"
                        options={connectionHealthStatuses.map(s => ({ label: s.name, value: s.value }))}
                        value={statusFilterValue}
                        onChange={onChangeStatusFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={false}
                        isDisabled={false}
                        isLoading={false}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Destination")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={destinationFilterValue}
                        onChange={onChangeDestinationUrl}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Provider Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Provider Type")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Destination Type")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Filter chips */}
            {(globalSearchStr || statusFilterValue || destinationFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue) &&
                <div className="flex flex-row flex-wrap gap-2 mb-3">
                    {globalSearchStr && <span className={filterClasses.tag}>
                        {globalSearchStr}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeGlobalSearch('')}><Clear /></span>
                    </span>}
                    {statusFilterValue && <span className={filterClasses.tag}>
                        {(statusFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeStatusFilter(null)}><Clear /></span>
                    </span>}
                    {destinationFilterValue && <span className={filterClasses.tag}>
                        {(destinationFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeDestinationUrl(null)}><Clear /></span>
                    </span>}
                    {ownerFilterValue && <span className={filterClasses.tag}>
                        {(ownerFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeOwner(null)}><Clear /></span>
                    </span>}
                    {providerTypeFilterValue && <span className={filterClasses.tag}>
                        {(providerTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeProviderType(null)}><Clear /></span>
                    </span>}
                    {destinationTypeFilterValue && <span className={filterClasses.tag}>
                        {(destinationTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeDestinationType(null)}><Clear /></span>
                    </span>}
                </div>
            }
            {/* Table */}
            <div className="flex flex-col w-full">
                    <table className={tableClasses.container}>
                        <thead className={tableClasses.header}>
                            <tr>
                                <th className={`${tableClasses.headerCell} w-28 flex flex-row align-top`}>
                                    {t("Health")}
                                    <span className="ml-1 cursor-pointer font-normal normal-case -mb-1.5">
                                        <Tooltip
                                            position="bottomleft"
                                            width="w-52"
                                            ariaLabel={t("Health status legend")}
                                            description={<HealthLegend t={t} />}
                                        />
                                    </span>
                                </th>
                                <th className={tableClasses.headerCell} style={{ maxWidth: "300px" }}>
                                    {t("Name")}
                                    <span className={tableClasses.sortIcon} onClick={() => {
                                        requestSort("name", sortDirection)
                                        setSortDirection(!sortDirection)
                                        setSortByColumn("name")
                                    }}>
                                        {sortByColumn === "name" ?
                                            sortDirection ? <SortedUp /> : <SortedDown />
                                            : <SortingArrows />}
                                    </span>
                                </th>
                                <th className={tableClasses.headerCell}>
                                    {t("Technology")}
                                    <span className={tableClasses.sortIcon} onClick={() => {
                                        requestSort("type__name", sortDirection)
                                        setSortDirection(!sortDirection)
                                        setSortByColumn("type__name")
                                    }}>
                                        {sortByColumn === "type__name" ?
                                            sortDirection ? <SortedUp /> : <SortedDown />
                                            : <SortingArrows />}
                                    </span>
                                </th>
                                <th className={tableClasses.headerCell}>
                                    {t("Destinations")}
                                </th>
                                <th className={tableClasses.headerCell}>
                                    {t("Workspace")}
                                    <span className={tableClasses.sortIcon} onClick={() => {
                                        requestSort("owner__name", sortDirection)
                                        setSortDirection(!sortDirection)
                                        setSortByColumn("owner__name")
                                    }}>
                                        {sortByColumn === "owner__name" ?
                                            sortDirection ? <SortedUp /> : <SortedDown />
                                            : <SortingArrows />}
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        {(!pageLoaded || reloadConnections) ?
                            <tbody className="text-sm">
                                {[...Array(8)].map((_, i) => (
                                    <tr key={i} className={tableClasses.row} data-testid="loading">
                                        <td className={`${tableClasses.cell} w-28`}>
                                            <div className="h-4 w-6 bg-neutral-gray-50 rounded animate-pulse mx-auto" />
                                        </td>
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-40 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-32 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-32 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-24 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                                :
                                <tbody className="text-sm cursor-pointer">
                                    {connections.map((connection: Connection) => {
                                        const connectionStatus = getConnectionStatus(connection.status, false, t);
                                        return (
                                            <tr key={connection.id}
                                                className={tableClasses.row}>
                                                <td className={`${tableClasses.cell} w-28`} onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/connections/${connection.id}`, '_blank') : onSelectConnection(connection.id)}>
                                                    <span title={connectionStatus.text} aria-label={connectionStatus.text} className="flex items-center pl-5">
                                                        {connectionStatus.icon}
                                                    </span>
                                                </td>
                                                <td className={tableClasses.cell} style={{ maxWidth: "300px" }} onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/connections/${connection.id}`, '_blank') : onSelectConnection(connection.id)}>
                                                    <span className="block truncate" title={connection.provider.name}>
                                                        {connection.provider.name}
                                                    </span>
                                                </td>
                                                <td className={tableClasses.cell} onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/connections/${connection.id}`, '_blank') : onSelectConnection(connection.id)}>
                                                    {connection.provider.type.name}
                                                </td>
                                                <td className={`${tableClasses.cell} text-blue-basic`}>
                                                    {connection.destinations.map((dest: Destination) => {
                                                        let dest_url: string | URL;
                                                        try {
                                                            dest_url = new URL(dest.base_url).hostname
                                                        } catch (err) {
                                                            dest_url = ""
                                                        }
                                                        return (
                                                            <div key={dest.id} className="flex items-center gap-2">
                                                                <span className="hover:underline cursor-pointer" onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/destination/${dest.id}/configuration`, '_blank') : onViewDetails(dest.id)}>{dest_url}</span>
                                                                {dest.base_url && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigator.clipboard.writeText(dest.base_url);
                                                                            toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                                position: toast.POSITION.TOP_RIGHT,
                                                                                autoClose: 2000
                                                                            });
                                                                        }}
                                                                        className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                                        aria-label="Copy URL to clipboard"
                                                                    >
                                                                        <CopyToClipboard />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </td>
                                                <td className={tableClasses.cell} onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/connections/${connection.id}`, '_blank') : onSelectConnection(connection.id)}>
                                                    {connection.owner.name}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>}
                        {!pageLoaded || reloadConnections ? null : connections.length === 0 &&
                            <tbody><tr><td colSpan={5} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                />
                            </td></tr></tbody>}
                        </table>
                    {totalCount > page_size && <Pagination
                        showText={true}
                        handlePageClick={onPageClicked}
                        pageCount={Math.ceil(totalCount / take)}
                        resultStart={offset + 1}
                        resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                        totalCount={totalCount}
                        initialPage={Math.floor(offset / take)}
                        take={take}
                        loading={!pageLoaded}
                        mainclassname={paginationClasses.container}
                    />}
            </div>
        </div>
    )
};

export default ConnectionsList;
